import { SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { createSkyPilotDynamics } from './130-sky-pilot-dynamics.mjs';
import { createSkyPilotCues } from './137-sky-pilot-cues.mjs';
import { createSkyEquipment } from './120-sky-equipment.mjs';
import { skyDirectAimView } from '../../../packages/domain/src/80-sky-aim.mjs';
import { createSkyObserverHead } from './161-sky-interior-camera.mjs';

/** Own one pilot mixer, equipment and active-time pose. The caller owns the
 * imported asset and world position; disposal releases only pilot-local gear. */
export function createSkyPilot(T, asset, { reduced = () => false } = {}) {
  const model = asset.scene;
  let provider = false;
  model.traverse((node) => {
    if (!node.userData.skyProviderRig) return;
    provider = true;
    model.userData.skyEquipmentTransforms = node.userData.skyEquipmentTransforms;
  });
  model.rotation.y = Math.PI;
  model.scale.setScalar(SKY_WORLD_ASSETS.pilot.modelScale);
  const group = new T.Group();
  group.add(model);
  const gear = createSkyEquipment(T, model);
  group.userData.skyWeaponMuzzleBind = gear.muzzleBind;
  group.userData.skyDualMuzzleBind = gear.dualMuzzleBind;
  group.userData.skyBoosterBinds = gear.boosterBinds;
  group.userData.skyEquipmentTransforms = model.userData.skyEquipmentTransforms;
  const mixer = new T.AnimationMixer(model);
  const actions = Object.fromEntries(
    asset.animations.map((clip) => [clip.name, mixer.clipAction(clip)]),
  );
  for (const action of Object.values(actions)) {
    action.play();
    action.setEffectiveWeight(0);
  }
  const names = [
    'root',
    'spine',
    'head',
    'upper_armL',
    'upper_armR',
    'thighL',
    'thighR',
    'shinL',
    'shinR',
    'forearmL',
    'forearmR',
    'footL',
    'footR',
  ];
  const bones = Object.fromEntries(names.map((name) => [name, model.getObjectByName(name)]));
  const observer = createSkyObserverHead(T, model, bones.head);
  const quietHead = bones.head.quaternion.clone();
  const base = new Map();
  const travel = { turn: 0, climb: 0, bodyTurn: 0, legTurn: 0, bodyClimb: 0, legClimb: 0 };
  group.userData.skyTravel = travel;
  const dynamics = createSkyPilotDynamics();
  const cues = createSkyPilotCues();
  const cueActions = {};
  for (const name of ['HitBody', 'Land', 'Stumble']) {
    const source = asset.animations.find((clip) => clip.name === name);
    if (!source) continue;
    const clip = source.clone();
    clip.name = `performance-${name}`;
    clip.tracks = clip.tracks.filter((track) =>
      /^(spine|head|upper_arm|forearm|mixamorig(Spine|Neck|.*Shoulder|.*Hand))/.test(track.name),
    );
    T.AnimationUtils.makeClipAdditive(clip, 0);
    cueActions[name] = mixer.clipAction(clip).play().setEffectiveWeight(0);
  }
  const performanceDurations = Object.fromEntries(
    asset.animations.map((clip) => [clip.name, clip.duration]),
  );
  const fire = asset.animations.find((clip) => clip.name === 'Fire');
  const fireTracks =
    provider && fire
      ? fire.tracks
          .filter((track) => /^(upper_armR|forearmR|mixamorigLeft(Shoulder|Hand))/.test(track.name))
          .map((track) => ({
            bone: model.getObjectByName(track.name.split('.')[0]),
            property: track.name.split('.')[1],
            sample: track.createInterpolant(),
          }))
      : [];
  const fireQuaternion = new T.Quaternion();
  const fireVector = new T.Vector3();
  const aimOrigin = new T.Vector3(),
    aimDirection = new T.Vector3(),
    barrelDirection = new T.Vector3();
  const weaponWorld = new T.Quaternion(),
    reactionHead = new T.Quaternion();
  const fit = new T.Matrix4().fromArray(
    model.userData.skyEquipmentTransforms?.forearmR ?? [
      1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
    ],
  );
  let weaponInverse;
  model.traverse((node) => {
    if (!provider || weaponInverse || !node.isSkinnedMesh) return;
    const index = node.skeleton.bones.findIndex((bone) => bone.name === 'forearmR');
    if (index >= 0) weaponInverse = node.skeleton.boneInverses[index];
  });
  const barrelLocal = new T.Vector3(0, -1, 0).transformDirection(fit);
  if (weaponInverse) barrelLocal.transformDirection(weaponInverse);
  let muzzleSample = null;
  let observerBarrels = 1;
  const muzzlePoint = new T.Vector3();
  function presentWeapon(state) {
    if (
      !provider ||
      !fire ||
      state.presentationPose ||
      state.traversal ||
      (state.status !== 'playing' && !skyDirectAimView(state))
    ) {
      return;
    }
    const bolt = state.shots?.findLast((shot) => shot.source === 'player');
    const speed = bolt
      ? Math.hypot(bolt.vx ?? 0, bolt.vy ?? 0, bolt.vz ?? -(bolt.speed ?? 120))
      : 1;
    const shotAge = bolt ? (bolt.distance ?? 0) / Math.max(1, speed) : 1;
    // Hold the provider's settled aiming pose. Replaying its whole gesture for
    // each bolt swept the elbow and made the muzzle look like a throwing hand.
    for (const track of fireTracks) {
      if (!track.bone) continue;
      const value = track.sample.evaluate(0.05);
      if (track.property === 'quaternion')
        track.bone.quaternion.copy(fireQuaternion.fromArray(value));
      else if (track.property === 'position') track.bone.position.copy(fireVector.fromArray(value));
    }
    // Lower and open the real elbow in the eye view. The complete cannon and
    // measured muzzle follow this arm; no separate scaled viewmodel is drawn.
    if (skyDirectAimView(state)) {
      group.updateWorldMatrix(true, true);
      const shoulder = bones.upper_armR;
      shoulder.getWorldPosition(aimOrigin);
      bones.forearmR.getWorldPosition(barrelDirection).sub(aimOrigin);
      aimDirection.copy(barrelDirection);
      fireVector
        .set(-0.32, -0.12, 0.47)
        .applyQuaternion(group.getWorldQuaternion(modelOrientation));
      aimDirection.add(fireVector).normalize();
      twist.setFromUnitVectors(barrelDirection.normalize(), aimDirection);
      shoulder.getWorldQuaternion(weaponWorld);
      shoulder.parent.getWorldQuaternion(parentInverse).invert();
      shoulder.quaternion.copy(parentInverse).multiply(twist).multiply(weaponWorld).normalize();
      shoulder.updateMatrixWorld(true);
    }
    const kick = !reduced() && shotAge < 0.09 ? Math.sin((Math.PI * shotAge) / 0.09) * 0.018 : 0;
    rotate('upper_armR', kick, 0, 0);
    group.updateMatrixWorld(true);
    const weapon = bones.forearmR;
    if (skyDirectAimView(state) && state.upgrades?.dual) {
      weapon.getWorldQuaternion(weaponWorld);
      barrelDirection.copy(barrelLocal).applyQuaternion(weaponWorld).normalize();
      fireVector
        .fromArray(gear.dualMuzzleBind)
        .sub(aimOrigin.fromArray(gear.muzzleBind))
        .transformDirection(weaponInverse)
        .applyQuaternion(weaponWorld);
      aimDirection.set(0, -1, 0).addScaledVector(barrelDirection, barrelDirection.y).normalize();
      twist.setFromUnitVectors(fireVector.normalize(), aimDirection);
      weapon.parent.getWorldQuaternion(parentInverse).invert();
      weapon.quaternion.copy(parentInverse).multiply(twist).multiply(weaponWorld).normalize();
      weapon.updateMatrixWorld(true);
    }
    // Solve from the actual tip, not the elbow. Three short corrections account
    // for the off-axis cannon mount without moving the airborne projectile.
    for (let solve = 0; solve < 3; solve++) {
      weapon.getWorldQuaternion(weaponWorld);
      aimOrigin
        .fromArray(gear.muzzleBind)
        .applyMatrix4(weaponInverse)
        .applyMatrix4(weapon.matrixWorld);
      if (state.visualAim)
        aimDirection
          .set(state.visualAim.x, state.visualAim.y, state.visualAim.z)
          .sub(aimOrigin)
          .normalize();
      else aimDirection.set(0, 0, 1).applyQuaternion(model.getWorldQuaternion(modelOrientation));
      barrelDirection.copy(barrelLocal).applyQuaternion(weaponWorld).normalize();
      twist.setFromUnitVectors(barrelDirection, aimDirection);
      weapon.parent.getWorldQuaternion(parentInverse).invert();
      weapon.quaternion.copy(parentInverse).multiply(twist).multiply(weaponWorld).normalize();
      weapon.updateMatrixWorld(true);
    }
  }
  const support = {
    shoulder: new T.Vector3(),
    elbow: new T.Vector3(),
    wrist: new T.Vector3(),
    target: new T.Vector3(),
    axis: new T.Vector3(),
    pole: new T.Vector3(),
    desired: new T.Vector3(),
    current: new T.Vector3(),
  };
  function supportWeapon(state) {
    if (!provider || !skyDirectAimView(state) || state.presentationPose) return;
    const upper = bones.upper_armL,
      lower = bones.forearmL;
    const hand = lower.children.find((node) => node.isBone);
    if (!hand) return;
    group.updateWorldMatrix(true, true);
    upper.getWorldPosition(support.shoulder);
    lower.getWorldPosition(support.elbow);
    hand.getWorldPosition(support.wrist);
    const upperLength = support.shoulder.distanceTo(support.elbow);
    const lowerLength = support.elbow.distanceTo(support.wrist);
    support.target
      .fromArray(gear.muzzleBind)
      .applyMatrix4(weaponInverse)
      .applyMatrix4(bones.forearmR.matrixWorld);
    // Both barrels are on the right gauntlet. The free left hand braces its
    // underside behind the hot muzzle, with real two-bone reach and no stretch.
    bones.forearmR.getWorldQuaternion(weaponWorld);
    support.current.copy(barrelLocal).applyQuaternion(weaponWorld);
    support.target.addScaledVector(support.current, -0.1);
    fireVector.set(0, -0.07, 0).applyQuaternion(group.getWorldQuaternion(modelOrientation));
    support.target.add(fireVector);
    support.axis.copy(support.target).sub(support.shoulder);
    const distance = T.MathUtils.clamp(
      support.axis.length(),
      0.04,
      (upperLength + lowerLength) * 0.98,
    );
    support.axis.normalize();
    support.target.copy(support.shoulder).addScaledVector(support.axis, distance);
    support.pole.set(0.3, -1, 0).applyQuaternion(modelOrientation);
    support.pole.addScaledVector(support.axis, -support.pole.dot(support.axis)).normalize();
    const cosine = T.MathUtils.clamp(
      (upperLength ** 2 + distance ** 2 - lowerLength ** 2) / (2 * upperLength * distance),
      -1,
      1,
    );
    support.desired
      .copy(support.shoulder)
      .addScaledVector(support.axis, upperLength * cosine)
      .addScaledVector(support.pole, upperLength * Math.sqrt(1 - cosine ** 2));
    for (const [bone, child, target] of [
      [upper, lower, support.desired],
      [lower, hand, support.target],
    ]) {
      bone.getWorldPosition(aimOrigin);
      child.getWorldPosition(support.current).sub(aimOrigin).normalize();
      aimDirection.copy(target).sub(aimOrigin).normalize();
      twist.setFromUnitVectors(support.current, aimDirection);
      bone.getWorldQuaternion(weaponWorld);
      bone.parent.getWorldQuaternion(parentInverse).invert();
      bone.quaternion.copy(parentInverse).multiply(twist).multiply(weaponWorld).normalize();
      bone.updateMatrixWorld(true);
    }
  }
  const rotationEuler = new T.Euler();
  const modelOrientation = new T.Quaternion();
  let lastTime = 0;
  let lastX = 0;
  let lastY = 0;
  let lastReduced = reduced();
  let initialized = false;
  let previousMode = null;
  let previousPresentation = null;
  let presentationYaw = 0;
  const vertical = new T.Vector3(0, 1, 0);
  const localAxis = new T.Vector3();
  const parentInverse = new T.Quaternion();
  const twist = new T.Quaternion();
  const aimBones = ['head', 'upper_armR', 'forearmR'];
  const aimRotations = aimBones.map(() => new T.Quaternion());
  function worldYaw(name, angle) {
    const bone = bones[name];
    bone.parent.getWorldQuaternion(parentInverse).invert();
    localAxis.copy(vertical).applyQuaternion(parentInverse);
    bone.quaternion.premultiply(twist.setFromAxisAngle(localAxis, angle));
    bone.updateMatrixWorld(true);
  }
  function presentShoulderView(state, dt, flight, quiet) {
    const requested =
      !state.traversal && Number.isFinite(state.viewYaw)
        ? T.MathUtils.clamp(state.viewYaw, -0.85, 0.85)
        : 0;
    presentationYaw =
      initialized && !quiet && !state.viewYawResolved && !state.traversal
        ? T.MathUtils.damp(presentationYaw, requested, 6, dt)
        : requested;
    if (Math.abs(presentationYaw) < 0.00001) return;
    group.updateMatrixWorld(true);
    aimBones.forEach((name, index) => bones[name].getWorldQuaternion(aimRotations[index]));
    // Right-shoulder camera sees the torso's opposite flank. Grounded hips
    // stay on the authored IK path so changing view never slides the feet.
    worldYaw('root', -presentationYaw * 0.35 * flight);
    worldYaw('spine', -presentationYaw * (1 - 0.35 * flight));
    // Keep head and weapon in their original world aim as the torso opens.
    aimBones.forEach((name, index) => {
      const bone = bones[name];
      bone.parent.getWorldQuaternion(parentInverse).invert();
      bone.quaternion.copy(parentInverse).multiply(aimRotations[index]).normalize();
      bone.updateMatrixWorld(true);
    });
  }
  function restorePose() {
    for (const [bone, rotation] of base) bone.quaternion.copy(rotation);
  }
  function rotate(name, x, y, z) {
    const bone = bones[name];
    if (!bone) return;
    if (provider) {
      // Provider bone roll is arbitrary. Express secondary motion in the model's
      // anatomical axes, then convert the delta into this joint's parent frame.
      model.getWorldQuaternion(modelOrientation);
      twist.setFromEuler(rotationEuler.set(x, y, z));
      twist.premultiply(modelOrientation).multiply(modelOrientation.invert());
      bone.parent.getWorldQuaternion(parentInverse).invert();
      twist.premultiply(parentInverse).multiply(parentInverse.invert());
      bone.quaternion.premultiply(twist).normalize();
      bone.updateMatrixWorld(true);
      return;
    }
    bone.rotation.x += x;
    bone.rotation.y += y;
    bone.rotation.z += z;
  }
  function animate(state) {
    observerBarrels = state.upgrades?.dual ? 2 : 1;
    gear.update(state.upgrades);
    gear.present(state.presentationPose, state.presentationAge);
    const time = state.time;
    const rawDelta = time - lastTime;
    const dt = Math.max(0, Math.min(0.1, rawDelta));
    const quiet = reduced();
    const cue = cues.sample(state, { reduced: quiet });
    if (
      initialized &&
      dt === 0 &&
      rawDelta >= 0 &&
      quiet === lastReduced &&
      state.presentationPose === previousPresentation
    )
      return;
    restorePose();
    if (rawDelta < 0) {
      initialized = false;
      for (const key of Object.keys(travel)) travel[key] = 0;
      mixer.setTime(0);
    }
    const mode =
      state.presentationPose && actions[state.presentationPose]
        ? state.presentationPose
        : state.status === 'ready' || state.locomotion === 'idle'
          ? 'Idle'
          : state.locomotion === 'walk'
            ? 'Walk'
            : state.locomotion === 'run'
              ? 'Run'
              : 'Flight';
    if (initialized && dt > 0) {
      // A scripted route already turns and banks the complete pilot along its
      // tangent. World-axis gameplay steering would twist the rig a second time.
      const turn =
        state.traversal || state.flightFrame
          ? 0
          : T.MathUtils.clamp((state.x - lastX) / rawDelta / 10, -1, 1);
      const climb =
        state.traversal || state.flightFrame
          ? 0
          : T.MathUtils.clamp((state.y - lastY) / rawDelta / 9, -1, 1);
      travel.turn = T.MathUtils.damp(travel.turn, turn, 9, dt);
      travel.climb = T.MathUtils.damp(travel.climb, climb, 9, dt);
      // Different response rates create a visible shoulder-to-hip-to-boot
      // inertial wave. Each follows actual movement, never a separate clock.
      travel.bodyTurn = T.MathUtils.damp(travel.bodyTurn, turn, 5, dt);
      travel.legTurn = T.MathUtils.damp(travel.legTurn, turn, 2.6, dt);
      travel.bodyClimb = T.MathUtils.damp(travel.bodyClimb, climb, 5, dt);
      travel.legClimb = T.MathUtils.damp(travel.legClimb, climb, 2.6, dt);
    }
    const grounded = mode === 'Walk' || mode === 'Run';
    if (grounded) {
      const current = actions[mode];
      const prior = actions[previousMode];
      const wasGrounded = previousMode === 'Walk' || previousMode === 'Run';
      if (initialized && mode !== previousMode) {
        if (wasGrounded) {
          // Match normalized stride phase before blending different durations;
          // otherwise a planted leg can blend with the opposite recovery leg.
          current.time = (prior.time / prior.getClip().duration) * current.getClip().duration;
        } else {
          // Both half-cycle boundaries are authored foot strikes. Land on the
          // closest one, retaining the running clip's athletic contact timing.
          const phase = current.time / current.getClip().duration;
          current.time = ((Math.round(phase * 2) / 2) % 1) * current.getClip().duration;
        }
      }
      for (const name of ['Walk', 'Run']) {
        const action = actions[name];
        const ratio = action.getClip().duration / current.getClip().duration;
        action.time = current.time * ratio;
        action.setEffectiveTimeScale(ratio);
      }
    } else {
      actions.Walk.setEffectiveTimeScale(1);
      actions.Run.setEffectiveTimeScale(1);
    }
    for (const [name, action] of Object.entries(actions)) {
      const goal = name === mode ? 1 : 0;
      action.setEffectiveWeight(
        initialized && !(quiet && mode !== previousMode) && !state.presentationPose
          ? T.MathUtils.damp(action.getEffectiveWeight(), goal, mode === 'Run' ? 18 : 10, dt)
          : goal,
      );
    }
    mixer.update(dt);
    if (state.presentationPose && actions[state.presentationPose]) {
      actions[mode].time = Math.min(
        actions[mode].getClip().duration,
        Math.max(0, state.presentationAge ?? 0),
      );
      mixer.update(0);
    }
    group.updateMatrixWorld(true);
    bones.head.getWorldQuaternion(reactionHead);
    for (const [name, action] of Object.entries(cueActions)) {
      action.setEffectiveWeight(cue?.name === name ? cue.weight : 0);
      if (cue?.name === name) action.time = cue.progress * action.getClip().duration;
    }
    if (Object.keys(cueActions).length) {
      mixer.update(0);
      // A recoil/stumble may bend the torso, but looking and aiming still track
      // the gameplay direction. Restore the pre-cue head in world coordinates.
      group.updateMatrixWorld(true);
      bones.head.parent.getWorldQuaternion(parentInverse).invert();
      bones.head.quaternion.copy(parentInverse).multiply(reactionHead);
    }
    for (const bone of Object.values(bones)) {
      if (!bone) continue;
      if (!base.has(bone)) base.set(bone, bone.quaternion.clone());
      else base.get(bone).copy(bone.quaternion);
    }
    const flight = actions.Flight?.getEffectiveWeight() ?? 0;
    // The authored flight now carries the full propulsion lean. Secondary
    // inertia follows it without overriding the grounded IK contact pose.
    if (!quiet && !state.presentationPose) {
      const turn = travel.turn;
      const climb = travel.climb;
      const sway = Math.sin(time * 2.2) * 0.014 * flight;
      // Look along travel/fire direction, then counterbalance the torso. All
      // additions are bounded and restored before the next authored clip pose.
      const bodyTurn = travel.bodyTurn;
      const legTurn = travel.legTurn;
      const bodyClimb = travel.bodyClimb;
      const legClimb = travel.legClimb;
      rotate(
        'root',
        -bodyClimb * 0.18 * flight,
        -bodyTurn * 0.12 * flight,
        bodyTurn * 0.24 * flight,
      );
      rotate('spine', -climb * 0.08 * flight, -turn * 0.08, turn * 0.13 + sway);
      rotate(
        'head',
        -climb * 0.11 + bodyClimb * 0.12 * flight,
        -turn * 0.18 + bodyTurn * 0.1 * flight,
        -turn * 0.1 - sway * 0.6,
      );
      rotate('upper_armL', climb * 0.13 * flight, -bodyTurn * 0.08 * flight, -turn * 0.09);
      rotate(
        'upper_armR',
        bodyClimb * 0.18 * flight,
        bodyTurn * 0.1 * flight,
        -bodyTurn * 0.14 * flight,
      );
      rotate('forearmL', -legClimb * 0.12 * flight, 0, 0);
      rotate('forearmR', -bodyClimb * 0.09 * flight, 0, bodyTurn * 0.06 * flight);
      rotate('thighL', -legClimb * 0.26 * flight, 0, (legTurn - bodyTurn) * 0.4 * flight);
      rotate('thighR', -legClimb * 0.2 * flight, 0, (legTurn - bodyTurn) * 0.34 * flight);
      rotate('shinL', legClimb * 0.22 * flight, 0, -legTurn * 0.08 * flight);
      rotate('shinR', legClimb * 0.16 * flight, 0, -legTurn * 0.08 * flight);
      rotate('footL', (legClimb - bodyClimb) * 0.18 * flight, 0, -legTurn * 0.12 * flight);
      rotate('footR', (legClimb - bodyClimb) * 0.18 * flight, 0, -legTurn * 0.12 * flight);
    }
    dynamics(state, mode, flight, travel, rotate, quiet || Boolean(state.presentationPose));
    if (quiet && !state.presentationPose) bones.head.quaternion.copy(quietHead);
    presentWeapon(state);
    presentShoulderView(state, dt, flight, quiet);
    supportWeapon(state);
    muzzleSample = null;
    if (
      provider &&
      weaponInverse &&
      (state.status === 'playing' || skyDirectAimView(state)) &&
      !state.presentationPose &&
      !state.traversal
    ) {
      group.updateMatrixWorld(true);
      muzzleSample = {
        time: state.time,
        barrels: [gear.muzzleBind, gear.dualMuzzleBind].map((point) => {
          muzzlePoint
            .fromArray(point)
            .applyMatrix4(weaponInverse)
            .applyMatrix4(bones.forearmR.matrixWorld);
          return {
            x: muzzlePoint.x - state.x,
            y: muzzlePoint.y - state.y,
            z: muzzlePoint.z - (state.pilotZ ?? 1),
          };
        }),
      };
    }
    previousMode = mode;
    previousPresentation = state.presentationPose;
    lastTime = time;
    lastX = state.x;
    lastY = state.y;
    lastReduced = quiet;
    initialized = true;
  }
  return {
    group,
    animate,
    performanceDurations,
    observerEye() {
      const result = observer.eye();
      if (!result || !provider || !weaponInverse) return result;
      bones.forearmR.updateWorldMatrix(true, false);
      return {
        ...result,
        barrels: [gear.muzzleBind, gear.dualMuzzleBind].slice(0, observerBarrels).map((point) => {
          muzzlePoint
            .fromArray(point)
            .applyMatrix4(weaponInverse)
            .applyMatrix4(bones.forearmR.matrixWorld);
          return { x: muzzlePoint.x, y: muzzlePoint.y, z: muzzlePoint.z };
        }),
      };
    },
    setObserverHeadMask: observer.setMask,
    weaponMuzzles: () =>
      muzzleSample
        ? { time: muzzleSample.time, barrels: muzzleSample.barrels.map((point) => ({ ...point })) }
        : null,
    dispose() {
      observer.dispose();
      gear.dispose();
      restorePose();
      base.clear();
      mixer.stopAllAction();
      mixer.uncacheRoot(model);
    },
  };
}
